import { Node, mergeAttributes } from "@tiptap/core"
import { ensureSafeLinkHref } from "./link-utils"

export type EmbedIframeAttrs = {
  src: string
  width?: string
  height?: string
  allow?: string
  frameborder?: string
  allowfullscreen?: string
  title?: string
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    embedIframe: {
      setEmbedIframe: (attrs: EmbedIframeAttrs) => ReturnType
    }
  }
}

export const EmbedIframe = Node.create({
  name: "embedIframe",
  group: "block",
  atom: true,
  selectable: true,
  draggable: true,
  addAttributes() {
    return {
      src: { default: "" },
      width: { default: "100%" },
      height: { default: "360" },
      allow: {
        default:
          "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      },
      frameborder: { default: "0" },
      allowfullscreen: { default: "true" },
      title: { default: "Embedded content" }
    }
  },
  parseHTML() {
    return [{ tag: "iframe[src]" }]
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "iframe",
      mergeAttributes(HTMLAttributes, {
        loading: "lazy"
      })
    ]
  },
  addCommands() {
    return {
      setEmbedIframe:
        (attrs) =>
        ({ commands }) =>
          commands.insertContent({
            type: this.name,
            attrs
          })
    }
  }
})

export function parseEmbedInput(value: string): EmbedIframeAttrs | null {
  const raw = value.trim()
  if (!raw) return null

  if (raw.includes("<")) {
    const doc = new DOMParser().parseFromString(raw, "text/html")
    const iframe = doc.querySelector("iframe")
    if (iframe) {
      const rawSrc = iframe.getAttribute("src")?.trim()
      const src = rawSrc ? ensureSafeLinkHref(rawSrc) : null
      if (!src) return null
      return {
        src,
        width: iframe.getAttribute("width") || "100%",
        height: iframe.getAttribute("height") || "360",
        allow:
          iframe.getAttribute("allow") ||
          "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share",
        frameborder: iframe.getAttribute("frameborder") || "0",
        allowfullscreen: iframe.hasAttribute("allowfullscreen")
          ? "true"
          : iframe.getAttribute("allowfullscreen") || "true",
        title: iframe.getAttribute("title") || "Embedded content"
      }
    }
  }

  const href = ensureSafeLinkHref(raw)
  if (!href) return null

  return {
    src: href,
    width: "100%",
    height: "360",
    frameborder: "0",
    allowfullscreen: "true",
    title: "Embedded content"
  }
}
